<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@/composables/api'
import { useAccess } from '@/composables/useAccess'
import { useAuth } from '@/composables/useAuth.ts'
import type { RegisteredCreditor } from './types'
import type { RouteLocationRaw } from 'vue-router'
import VismaNetCreditorRegistrationDialog from './VismaNetCreditorRegistrationDialog.vue'
import BTooltipBtn from '@/components/base/BTooltipBtn/BTooltipBtn.vue'
import mdiArrowRight from '~icons/mdi/arrow-right'
import mdiPlus from '~icons/mdi/plus'

const props = defineProps<{
  tabShowing: boolean
}>()

const { t } = useI18n()
const { canAccessResource } = useAccess()
const { isSolicitor, isSystemUser } = useAuth()

const page = ref(1)
const pageMaxResults = ref(8)
const showRegistrationDialog = ref(false)

const canAccessImportFilters = canAccessResource('partner--import-filters')

const partnerImportFiltersQuery = useQuery(
  '/partner--import-filters',
  computed(() => ({
    query: {
      max_results: pageMaxResults.value,
      page: page.value,
    },
  })),
  { enabled: () => props.tabShowing && canAccessImportFilters.value }
)

const partnerImportFilters = computed(() => partnerImportFiltersQuery.data.value?._items ?? [])
const isLoading = computed(() => partnerImportFiltersQuery.isLoading.value)

const totalPages = computed(() => {
  const meta = partnerImportFiltersQuery.data.value?._meta
  if (meta == null) return 1
  const total = meta.total ?? 0
  const maxResults = meta.max_results ?? pageMaxResults.value
  return Math.ceil(total / maxResults)
})

const registrationDataQuery = useQuery(
  '/visma-net-registration--data',
  {},
  {
    enabled: computed(
      () => props.tabShowing && !isSolicitor.value && !isSystemUser.value && canAccessImportFilters.value
    ),
  }
)

const registeredCreditorsByInternalId = computed<Record<string, RegisteredCreditor>>(() => {
  const data = registrationDataQuery.data.value
  const creditors = data?.creditors_already_registered ?? []
  const map: Record<string, RegisteredCreditor> = {}
  for (const creditor of creditors) {
    if (creditor.internal_id != null) {
      map[creditor.internal_id] = creditor
    }
  }
  return map
})

const allCreditorsAreRegistered = computed(() => {
  const data = registrationDataQuery.data.value
  const registeredCount = data?.creditors_already_registered?.length ?? 0
  const availableCount = data?.creditors_data.length ?? 0
  return availableCount > 0 && registeredCount >= availableCount
})

function getCreditorSettingsRoute(creditorId: string, isEnabled: boolean): RouteLocationRaw {
  return {
    name: 'VismaNetSettings',
    params: { creditorId, mode: isEnabled ? 'update' : 'initial' },
  }
}
</script>

<template>
  <div class="pa-4">
    <div
      v-if="isLoading"
      class="d-flex justify-center py-4"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <template v-else>
      <v-card
        v-for="filter in partnerImportFilters"
        :key="filter._id"
        variant="outlined"
        class="pa-4 d-flex justify-space-between align-center mb-4"
      >
        <span class="font-weight-bold">
          {{ registeredCreditorsByInternalId[filter.creditor]?.name ?? filter.creditor_name ?? filter.creditor }}
        </span>

        <div class="d-flex align-center ga-6">
          <v-chip
            :color="filter.is_enabled ? 'success' : 'default'"
            size="small"
          >
            {{ filter.is_enabled ? t('common.activated') : t('common.inactive') }}
          </v-chip>

          <b-tooltip-btn
            :to="getCreditorSettingsRoute(filter.creditor, filter.is_enabled === true)"
            tooltip="go_to_settings"
            :icon="mdiArrowRight"
            density="compact"
            variant="text"
          />
        </div>
      </v-card>

      <v-btn
        :disabled="allCreditorsAreRegistered"
        :prepend-icon="mdiPlus"
        variant="outlined"
        block
        class="mb-2 mt-3"
        @click="showRegistrationDialog = true"
      >
        {{ t('common.register_new') }}
      </v-btn>

      <v-pagination
        v-if="totalPages > 1"
        v-model="page"
        :length="totalPages"
        :total-visible="5"
        class="mt-4"
      />
    </template>

    <visma-net-creditor-registration-dialog
      v-if="!allCreditorsAreRegistered"
      v-model="showRegistrationDialog"
    />
  </div>
</template>
